import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { LocalSettingsService } from '../local-settings/local-settings.service';
import moment from 'moment';
import { BackendCallService } from '../backend-call/backend-call.service';
import { User } from '../user';
import { isNotEmptyArray } from 'src/app/utilities/utils';
import { Observable, of, Subject } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class UserAuthService {
  public authenticated: boolean;
  get userInfoUpdated$(): Observable<void> {
    return this.userInfoUpdatedSubject.asObservable();
  }
  private readonly userInfoUpdatedSubject = new Subject<void>();
  private tokenRefreshService: any; // Will be injected later to avoid circular dependency
  private isLogoutInProgress = false;
  private isLoggingOut = false;

  constructor(private oidcSecurityService: OidcSecurityService, private authService: MsalService, public localSettingsSrv: LocalSettingsService,
    public backendService: BackendCallService) { }

  /**
   * Set token refresh service (called from app initialization)
   */
  public setTokenRefreshService(tokenRefreshService: any): void {
    this.tokenRefreshService = tokenRefreshService;
  }

  hasLibraryListPermission(): boolean {
    return this.hasPermission('Library.List');
  }

  // getUserInfo() {
  //   let userInfo = {
  //     userId: '',
  //     email: '',
  //     auth_time: '',
  //     roles: []
  //   }
  //   const user = this.oidcSecurityService.getUserData();
  //   if (user) {
  //     userInfo.email = user.sub;
  //     userInfo.roles = user.role
  //     userInfo.auth_time = user.auth_time
  //     userInfo.userId = user.id
  //   }
  //   return userInfo;
  // }


  getUserList() {
    const user = {
      name: '',
      userId: '',
      email: '',
      jobTitle:'',
      jobTitleReport: '',
      organization:'',
      country: '',
      region: '',
      phone: '',
      language: '',
      thumbnailUrl: '',
      qualifications: '',
      isGlobalAdmin: false,
      profileData: {},
      permissions: [],
      roles: []
    };
    const userInfo = this.getUserInfoFromLocal();
    if (userInfo) {
      user.name = userInfo.name;
      user.userId = userInfo.userId;
      user.email = userInfo.email;     
      user.profileData = userInfo.profileData;
      user.jobTitle= userInfo.jobTitle;
      user.jobTitleReport= userInfo.jobTitleReport;
      user.organization = userInfo.organization;;
      user.country= userInfo.country;
      user.region= userInfo.region;
      user.phone= userInfo.phone;
      user.language= userInfo.language;
      user.thumbnailUrl= userInfo.thumbnailUrl;
      user.qualifications= userInfo.qualifications;
      user.permissions = userInfo.permissions || userInfo.roleAssignmentInfo;
      user.roles = userInfo?.roles;
      if (userInfo.isGlobalAdmin) {user.isGlobalAdmin = userInfo.isGlobalAdmin;}
    }
    return user;
  }

  isGlobalAdmin() {
    let isGlobalAdminRole = false;
    const user = this.getUserList();
    if (user && user.isGlobalAdmin) {isGlobalAdminRole = user.isGlobalAdmin;}
    return isGlobalAdminRole;
  }

  getAccessToken() {
    let access_Token;
    //return this.oidcSecurityService.getAccessToken();
    const user = this.getUserInfoFromLocal();
    if (user && user.accessToken) {access_Token = user.accessToken;}
    return access_Token;
  }

  getMsalAccessToken() {
    let access_Token = '';
    const user = this.getUserInfoFromLocal();
    if (user && user.msal_accessToken) {access_Token = user.msal_accessToken;}
    return access_Token;
  }

  getAuthenticationInfo() {
    return this.oidcSecurityService.getAuthenticationResult();
  }

  login() {
    this.oidcSecurityService.authorize();
  }

  logOut() {
    if (this.isLogoutInProgress) {
      return;
    }

    this.isLogoutInProgress = true;
    //this.oidcSecurityService.logoff();
    try {
      const logoutResult: any = this.authService.logout();
      if (logoutResult?.subscribe) {
        logoutResult.subscribe({
          error: (error) => this.handleLogoutError(error)
        });
      } else if (logoutResult?.catch) {
        logoutResult.catch((error) => this.handleLogoutError(error));
      }
    } catch (error) {
      this.handleLogoutError(error);
    }
    // Guard against multiple concurrent logout attempts (e.g. several parallel
    // API calls all returning 401 at once), which can leave the app in a
    // half-cleared, stuck state.
    if (this.isLoggingOut) { return; }
    this.isLoggingOut = true;

    // Clear application storage first so no stale/expired session data remains.
    this.localSettingsSrv.clearAllLocalStorageInfo();

    const account = this.authService.instance.getActiveAccount()
      || this.authService.instance.getAllAccounts()[0];

    // MsalService.logout() returns a COLD Observable — it must be subscribed for
    // MSAL to actually clear its cache and perform the redirect. Previously it
    // was called without subscribing, so nothing happened: the app storage was
    // wiped but the browser stayed on the protected page (and the MSAL cache was
    // left behind), leaving the user stuck on a blank screen until they cleared
    // storage manually. We also force a navigation to /login as a safety net in
    // case the MSAL redirect does not fire.
    this.authService.logout({
      account,
      postLogoutRedirectUri: window.location.origin + '/login'
    }).subscribe({
      next: () => this.forceRedirectToLogin(),
      error: () => this.forceRedirectToLogin()
    });
  }

  private forceRedirectToLogin() {
    this.isLoggingOut = false;
    // Guarantee the user leaves the protected page even if the MSAL redirect did
    // not navigate. A full reload also resets any in-memory state tied to the
    // now-expired session.
    if (!window.location.pathname.includes('/login')) {
      window.location.replace('/login');
    }
  }

  private handleLogoutError(error: any): void {
    this.isLogoutInProgress = false;
    if (error?.errorCode === 'interaction_in_progress' || error?.message?.includes('interaction_in_progress')) {
      console.warn('Logout skipped because an authentication interaction is already in progress.', error);
      return;
    }

    console.error('Logout failed:', error);
  }

  checkMsalAuth() {
    return this.authService.instance.getAllAccounts().length > 0;
  }

  checkUserExisting(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const user = this.getUserInfoFromLocal();
      
      if (!user) {
        observer.next(false);
        observer.complete();
        return;
      }

      // Check if token is still valid (with 5-minute buffer)
      if (user.accessToken && moment().add(5, 'minutes').isBefore(user.msal_expiry)) {
        // The app token looks fresh, but it is stored separately from the MSAL
        // token cache. Verify that the underlying MSAL cache has not been
        // tampered with / corrupted before trusting the session — otherwise a
        // corrupted msal.* cache would go undetected until a later refresh.
        this.validateMsalCacheIntegrity(observer);
      } else if (user.msal_expiry && moment().isAfter(moment(user.msal_expiry).subtract(10, 'minutes'))) {
        // Token is expiring soon or expired, try to refresh
        if (this.tokenRefreshService) {
          this.tokenRefreshService.silentRefreshToken().subscribe(
            (refreshSuccess) => {
              if (refreshSuccess) {
                observer.next(true);
              } else {
                // Refresh failed, logout user
                this.logOut();
                observer.next(false);
              }
              observer.complete();
            },
            (error) => {
              console.error('Token refresh error:', error);
              this.logOut();
              observer.next(false);
              observer.complete();
            }
          );
        } else {
          // No token refresh service available, logout
          this.logOut();
          observer.next(false);
          observer.complete();
        }
      } else {
        observer.next(false);
        observer.complete();
      }
    });
  }

  /**
   * Verify the MSAL token cache is intact for an otherwise-fresh session. If it
   * has been corrupted/tampered with, clear everything and log the user out;
   * otherwise resolve the session as valid.
   */
  private validateMsalCacheIntegrity(observer): void {
    if (this.tokenRefreshService && typeof this.tokenRefreshService.validateMsalCacheIntegrity === 'function') {
      this.tokenRefreshService.validateMsalCacheIntegrity().subscribe(
        (isIntact: boolean) => {
          if (isIntact) {
            observer.next(true);
          } else {
            this.logOut();
            observer.next(false);
          }
          observer.complete();
        },
        () => {
          // Never block navigation on a validation failure itself.
          observer.next(true);
          observer.complete();
        }
      );
    } else {
      observer.next(true);
      observer.complete();
    }
  }

  // Legacy method for backward compatibility - now returns boolean synchronously
  checkUserExistingSync(): boolean {
    const user = this.getUserInfoFromLocal();
    if (user) {
      if (user.accessToken && moment().isBefore(user.msal_expiry)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get a valid access token (will refresh if needed)
   */
  getValidAccessToken(): Observable<string> {
    return new Observable<string>(observer => {
      const user = this.getUserInfoFromLocal();
      
      if (!user) {
        observer.error('No user found');
        return;
      }

      // Check if current token is valid for at least 5 minutes
      if (moment().add(5, 'minutes').isBefore(user.msal_expiry)) {
        observer.next(user.msal_accessToken || user.accessToken);
        observer.complete();
      } else {
        // Token is expiring soon, refresh it
        if (this.tokenRefreshService) {
          this.tokenRefreshService.silentRefreshToken().subscribe(
            (success) => {
              if (success) {
                const updatedUser = this.getUserInfoFromLocal();
                observer.next(updatedUser.msal_accessToken || updatedUser.accessToken);
              } else {
                observer.error('Token refresh failed');
              }
              observer.complete();
            },
            (error) => {
              observer.error(error);
            }
          );
        } else {
          observer.error('Token refresh service not available');
        }
      }
    });
  }

  storeUserInfo(userAuthInfo, msalAuthInfo) {
    const body = {
      msal_accessToken: msalAuthInfo.msal_accesstoken,
      msal_tenantId: msalAuthInfo.msal_account.tenantId,
      msal_userName: msalAuthInfo.msal_account.username,
      msal_expiry: msalAuthInfo.msal_expiry,
      accessToken: userAuthInfo.accssToken,
      userId: userAuthInfo.userInfo.Id,
      name: userAuthInfo.userInfo.Display_Name,
      email: userAuthInfo.userInfo.Email_Address,
      roles: userAuthInfo?.userInfo?.Roles,
      qualifications: userAuthInfo.userInfo.Qualifications,
      region: userAuthInfo.userInfo.Region,
      country: userAuthInfo.userInfo.Country,
      phone: userAuthInfo.userInfo.Phone_Number,
      language: userAuthInfo.userInfo.Languages,
      thumbnailUrl: userAuthInfo.userInfo.User_Image,
      permissions: userAuthInfo.userInfo.Permissions,
      jobTitle: userAuthInfo.userInfo.Job_Title,
      jobTitleReport: userAuthInfo.userInfo.Job_Title_Report,
      organization: userAuthInfo.userInfo.Organization,
      profileData: userAuthInfo.userInfo.Profile_Data,
      isGlobalAdmin: userAuthInfo.userInfo.IsGlobalAdmin || false,
      tid: userAuthInfo.userInfo.TID
    };
    this.localSettingsSrv.storeUserInfo('userLists', body);
    this.userInfoUpdatedSubject.next();
  }

  getUserInfoFromLocal() {
    return this.localSettingsSrv.getuserInfo('userLists');
  }

  hasPermission(permissionName: string): boolean {
    const normalizedPermission = (permissionName || '').trim().toUpperCase();
    if (!normalizedPermission) {
      return false;
    }

    const permissions = this.getUserList()?.permissions ?? [];
    return Array.isArray(permissions) && permissions.some((permission: any) =>
      String(permission ?? '').trim().toUpperCase() === normalizedPermission
    );
  }

  getToken() {
    return this.backendService.getToken();
  }
}
